"use client"

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react"

import { CalendarMonth } from "@/components/graphite/calendar-month"
import { ChipPopover } from "@/components/graphite/chip-popover"
import {
  addMonths,
  monthOf,
  monthOfToday,
  orderRange,
  rangeLabel,
  shiftDays,
  shiftMonths,
  todayIso,
  type YearMonth,
} from "@/lib/calendar"
import { cn } from "@/lib/utils"

/**
 * A date range, picked — canvas 31a.
 *
 * This replaces two chips that each hid a transparent `<input type="date">`
 * over their face. That arrangement had a real cost: the target was a box the
 * browser drew somewhere of its own choosing, the value read back in whatever
 * format the platform preferred, and the two ends of one range were two
 * unrelated controls with no idea the other existed. 31a makes it one thing —
 * "you pick the first bound, then the second, and the chip states the result".
 *
 * The panel is deliberately bare: two months, and *no fields, no footer, no
 * rail*. It applies on the second pick and closes itself; the presets stay
 * where they already are, in the filter row's segment.
 *
 * One thing 31a does not draw is kept anyway: the clear. The chips this
 * replaces could each be emptied on their own, and a filter that can be turned
 * on but not off is a worse control however it looks — so a set chip carries an
 * ×. Everything else is the artboard.
 */

/** How far each arrow moves, in days. A week is a row. */
const STEP: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
}

/**
 * The two months and the line above them.
 *
 * A component of its own, mounted with the panel, so that everything a pick in
 * progress needs — which month is on screen, which bound has landed, where the
 * keyboard is standing — begins fresh each time the panel opens and is gone
 * when it closes. Held in the chip instead, all of it would need resetting by
 * hand, and whichever one got forgotten would reopen mid-pick.
 */
function RangePanel({
  from,
  to,
  today,
  onCommit,
}: {
  from: string
  to: string
  today: string
  onCommit: (next: { from: string; to: string }) => void
}) {
  const [cursor, setCursor] = useState<YearMonth>(() =>
    monthOf(from || to, monthOf(today, monthOfToday())),
  )
  /** The first bound of a range being picked, before the second lands. */
  const [anchor, setAnchor] = useState<string>()
  /**
   * The day under the cursor, which is the second bound while one is being
   * picked. It is never cleared on the way out of a month — crossing the gap
   * between the two would otherwise collapse the preview for a frame.
   */
  const [hover, setHover] = useState<string>()
  /**
   * The one day in the panel that holds the keyboard. The grid is a single tab
   * stop and the arrows move within it — forty-two tab stops between a filter
   * and the next control is its own kind of unusable.
   */
  const [focusDay, setFocusDay] = useState(from || to || today)

  const grid = useRef<HTMLDivElement>(null)

  /*
    While a range is being drawn the panel shows the pick in progress, not the
    range that is still applied — otherwise the first click appears to do
    nothing until the second one lands.
  */
  const [drawnFrom, drawnTo] = anchor
    ? orderRange(anchor, hover ?? anchor)
    : [from || undefined, to || undefined]

  /*
    The focused day is put under the keyboard on mount and on every arrow.
    `data-day` marks the in-month cell only, so this finds the one grid that
    owns the date rather than a borrowed copy of it.
  */
  useEffect(() => {
    grid.current
      ?.querySelector<HTMLElement>(`[data-day="${focusDay}"]`)
      ?.focus({ preventScroll: true })
  }, [focusDay, cursor])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const months = event.key === "PageUp" ? -1 : event.key === "PageDown" ? 1 : 0
    if (months !== 0) {
      event.preventDefault()
      const next = shiftMonths(focusDay, months)
      setCursor(monthOf(next, cursor))
      setFocusDay(next)
      return
    }

    const step = STEP[event.key]
    if (step === undefined) return
    event.preventDefault()

    const next = shiftDays(focusDay, step)
    /*
      The cell that *owns* this date, in the month it belongs to. Missing or
      hidden means the panel is not showing that month — which below @3xl
      includes the second one, since it is `display:none` there and cannot take
      focus. Asking the DOM rather than the breakpoint keeps this right at both
      widths.
    */
    const cell = grid.current?.querySelector<HTMLElement>(`[data-day="${next}"]`)
    if (!cell || cell.offsetParent === null) setCursor(monthOf(next, cursor))
    setFocusDay(next)
  }

  /**
   * Both chevrons move the pair of months, and take the roving day with them —
   * stepping the view away from the focused day would leave the keyboard
   * standing on a cell nobody can see.
   */
  const step = (delta: number) => {
    setCursor(addMonths(cursor, delta))
    setFocusDay((current) => shiftMonths(current, delta))
  }

  const pick = (iso: string) => {
    // Wherever the pick came from, the keyboard now stands there: arrowing on
    // from a day you clicked should start at that day, not at whichever one the
    // panel opened on.
    setFocusDay(iso)
    if (!anchor) {
      setAnchor(iso)
      setHover(iso)
      return
    }
    const [start, end] = orderRange(anchor, iso)
    onCommit({ from: start, to: end })
  }

  return (
    <div className="flex flex-col px-[18px] py-4" onKeyDown={onKeyDown}>
      {/*
        31a draws the panel bare, and at two months wide the band across them
        says "range" on its own. In the ATS panel it is one month, and one month
        with nothing above it reads as a single-date picker — so the panel says
        which end it is waiting for. It states the range itself once both ends
        are in.
      */}
      <p className="mb-3 flex items-baseline gap-2 text-[11.5px] leading-none">
        <span className="font-medium text-muted-foreground">
          {anchor ? "Pick an end date" : "Pick a start date"}
        </span>
        <span
          className={cn(
            "ml-auto whitespace-nowrap font-mono text-[11.5px]",
            drawnFrom || drawnTo ? "text-brand-ink" : "text-muted-foreground",
          )}
        >
          {rangeLabel(drawnFrom, drawnTo)}
        </span>
      </p>

      {/*
        Two chevrons at the outer edges, moving both months together — the pair
        is one control, not two independent calendars.

        Below @3xl the panel would not fit beside the row it hangs from, so it
        shows the month you are on and keeps both steppers. 31c makes the same
        call for the phone: "a range on the phone is one month at a time".
      */}
      <div ref={grid} className="flex gap-[22px]">
        <CalendarMonth
          month={cursor}
          from={drawnFrom}
          to={drawnTo}
          today={today}
          onPick={pick}
          onHoverDay={(iso) => iso && setHover(iso)}
          focusedDay={focusDay}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          nextClassName="@3xl:invisible"
        />
        <CalendarMonth
          month={addMonths(cursor, 1)}
          from={drawnFrom}
          to={drawnTo}
          today={today}
          onPick={pick}
          onHoverDay={(iso) => iso && setHover(iso)}
          focusedDay={focusDay}
          onNext={() => step(1)}
          className="hidden @3xl:block"
        />
      </div>
    </div>
  )
}

export function DateRangeChip({
  label = "Dates",
  from,
  to,
  onChange,
  className,
  now,
}: {
  label?: string
  /** `yyyy-mm-dd`, or "" for an open end. */
  from: string
  to: string
  onChange: (next: { from: string; to: string }) => void
  className?: string
  /** Injectable so a test can pin "today". */
  now?: Date
}) {
  return (
    <ChipPopover
      label={label}
      value={rangeLabel(from, to)}
      active={Boolean(from || to)}
      onClear={() => onChange({ from: "", to: "" })}
      clearLabel="Clear the date range"
      haspopup="dialog"
      panelRole="dialog"
      panelLabel="Date range"
      className={className}
    >
      {({ close }) => (
        <RangePanel
          from={from}
          to={to}
          today={todayIso(now)}
          onCommit={(next) => {
            onChange(next)
            close()
          }}
        />
      )}
    </ChipPopover>
  )
}
